import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowRight } from 'lucide-react';
import {
  BarList,
  CountBadge,
  EmptyState,
  ErrorState,
  PersonCell,
  SectionCard,
  SegmentedControl,
  SpecGrid,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  useDelayedFlag,
} from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { formatCount, formatNumber } from '../../../utils/format';
import { asSentence, countWeekends, formatWeekendRange, formatWeekendSpan, holidayDayFromLabel } from './helpers';
import type { WeekendShiftAnalyticsData, WeekendShiftAnalyticsEmployeeSummary } from './types';

const TREND_COLUMNS = 9;
const QUICK_RANGES = [4, 8, 12] as const;

export interface HistoryTabProps {
  analyticsData: WeekendShiftAnalyticsData | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  historyStartWeekend: string;
  historyEndWeekend: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  onQuickRange: (weekendCount: number) => void;
  /** Open a past weekend's roster. */
  onViewWeekend: (weekendStartDate: string) => void;
  topSignupEmployees: WeekendShiftAnalyticsEmployeeSummary[];
  zeroSignupEmployees: WeekendShiftAnalyticsEmployeeSummary[];
  missedStartLeaders: WeekendShiftAnalyticsEmployeeSummary[];
  missedEndLeaders: WeekendShiftAnalyticsEmployeeSummary[];
}

const LeaderCard: React.FC<{
  title: string;
  description: string;
  items: WeekendShiftAnalyticsEmployeeSummary[];
  value: (employee: WeekendShiftAnalyticsEmployeeSummary) => number;
  display: (value: number) => string;
  caption: (employee: WeekendShiftAnalyticsEmployeeSummary) => string;
  emptyTitle: string;
  emptyBody: string;
  pending: boolean;
  /**
   * `bars` for counts with real spread (shifts signed up); `list` for small
   * integers (missed check-ins), where a bar would overstate a 1-vs-2 gap.
   */
  variant?: 'bars' | 'list';
}> = ({ title, description, items, value, display, caption, emptyTitle, emptyBody, pending, variant = 'bars' }) => {
  const showSkeleton = useDelayedFlag(pending);
  return (
    <SectionCard title={title} description={description}>
      {pending ? (
        <Box aria-hidden="true" sx={{ display: 'grid', gap: 1.5, visibility: showSkeleton ? 'visible' : 'hidden' }}>
          {[0, 1, 2, 3, 4].map((n) => (
            <Skeleton key={n} variant="rounded" height={18} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <EmptyState compact title={emptyTitle} body={emptyBody} />
      ) : variant === 'list' ? (
        <Box component="ol" aria-label={title} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {items.map((employee, index) => (
            <Box
              component="li"
              key={employee.employeeId}
              sx={{
                display: 'grid',
                gridTemplateColumns: '20px minmax(0, 1fr) auto',
                alignItems: 'baseline',
                columnGap: 1.5,
                py: 1,
                borderTop: index === 0 ? 0 : `1px solid ${sbg.border.subtle}`,
              }}
            >
              <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                {index + 1}
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="labelMd" component="span" sx={{ display: 'block', color: sbg.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {employee.name}
                </Typography>
                <Typography variant="body2" component="span" sx={{ display: 'block', color: sbg.text.secondary }}>
                  {`${employee.teamName || 'Sales'} · ${caption(employee)}`}
                </Typography>
              </Box>
              <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', textAlign: 'right' }}>
                {display(value(employee))}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <BarList
          items={items.map((employee) => {
            const amount = value(employee);
            return {
              key: employee.employeeId,
              label: employee.name,
              caption: caption(employee),
              value: amount,
              display: display(amount),
            };
          })}
        />
      )}
    </SectionCard>
  );
};

/** Signup and check-in history across a range of past weekends. */
const HistoryTab: React.FC<HistoryTabProps> = ({
  analyticsData,
  loading,
  error,
  onRetry,
  historyStartWeekend,
  historyEndWeekend,
  onStartChange,
  onEndChange,
  onQuickRange,
  onViewWeekend,
  topSignupEmployees,
  zeroSignupEmployees,
  missedStartLeaders,
  missedEndLeaders,
}) => {
  const firstLoad = !analyticsData && loading;
  const showSkeleton = useDelayedFlag(firstLoad);
  const summary = analyticsData?.summary;
  const weekendCount = countWeekends(historyStartWeekend, historyEndWeekend);
  const quickValue = (QUICK_RANGES as readonly number[]).includes(weekendCount) ? String(weekendCount) : 'custom';
  const span = analyticsData
    ? formatWeekendSpan(analyticsData.startWeekendDate, analyticsData.endWeekendDate)
    : formatWeekendSpan(historyStartWeekend, historyEndWeekend);

  const specItems = useMemo(() => {
    if (!summary) return [];
    const missedTotal = (summary.totalMissedStartCheckins || 0) + (summary.totalMissedEndCheckins || 0);
    return [
      { label: 'Weekends', value: formatCount(summary.totalWeekends, 'weekend'), caption: span },
      {
        label: 'Shift signups',
        value: formatCount(summary.totalShiftSignups, 'shift'),
        caption: `${formatCount(summary.totalSignedUpEmployees, 'rep')} signed up at least once`,
      },
      {
        label: 'No signups',
        value: formatCount(summary.totalZeroSignupEmployees, 'rep'),
        caption: `Of ${formatCount(summary.totalSalesReps, 'sales rep')}`,
      },
      {
        label: `Missed check-ins (${formatCount(summary.totalWeekends, 'weekend')})`,
        value: formatCount(missedTotal, 'check-in'),
        caption: `${formatNumber(summary.totalMissedStartCheckins)} start · ${formatNumber(summary.totalMissedEndCheckins)} end`,
      },
    ];
  }, [summary, span]);

  const failedFirstLoad = !analyticsData && Boolean(error);
  const leaderPending = firstLoad;

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {analyticsData && error && (
        <Alert
          severity="error"
          action={
            <Button size="small" variant="text" color="inherit" onClick={onRetry}>
              Try again
            </Button>
          }
        >
          {`Couldn’t refresh this range. ${asSentence(error)} Showing the last range that loaded.`}
        </Alert>
      )}

      <SectionCard
        title="Signups and check-ins"
        description={loading && analyticsData ? `Updating ${span}…` : `${span} · ${formatCount(weekendCount, 'weekend')}`}
      >
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="date"
              label="First weekend"
              value={historyStartWeekend}
              onChange={(event) => onStartChange(event.target.value)}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
            <TextField
              type="date"
              label="Last weekend"
              value={historyEndWeekend}
              onChange={(event) => onEndChange(event.target.value)}
              sx={{ width: { xs: '100%', sm: 180 } }}
            />
            <SegmentedControl
              aria-label="Quick range"
              value={quickValue}
              onChange={(next) => onQuickRange(Number(next))}
              options={QUICK_RANGES.map((count) => ({ value: String(count), label: `Last ${count}` }))}
            />
          </Box>
          {failedFirstLoad ? (
            <ErrorState compact title="Couldn’t load signup history" body={error ? asSentence(error) : undefined} onRetry={onRetry} />
          ) : firstLoad ? (
            <Box
              aria-hidden="true"
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                visibility: showSkeleton ? 'visible' : 'hidden',
              }}
            >
              {[0, 1, 2, 3].map((n) => (
                <Skeleton key={n} variant="rounded" height={88} />
              ))}
            </Box>
          ) : (
            <SpecGrid items={specItems} minColumnWidth={180} />
          )}
        </Box>
      </SectionCard>

      {!failedFirstLoad && (
        <TableCard
          aria-label="Weekend by weekend"
          toolbar={
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
                Weekend by weekend
              </Typography>
              <Typography variant="body2" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                Rep counts per weekend. Missed columns count check-ins.
              </Typography>
            </Box>
          }
        >
          <Table sx={{ minWidth: 900 }} aria-busy={loading || undefined}>
            <TableHead>
              <TableRow>
                <TableCell>Weekend</TableCell>
                <TableCell align="right">Signed up</TableCell>
                <TableCell align="right">No shifts</TableCell>
                <TableCell align="right">1 shift</TableCell>
                <TableCell align="right">2+ shifts</TableCell>
                <TableCell align="right">3+ shifts</TableCell>
                <TableCell align="right">Missed starts</TableCell>
                <TableCell align="right">Missed ends</TableCell>
                <TableCell align="right">
                  <span className="sbg-sr-only">Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firstLoad ? (
                <TableSkeletonRows columns={TREND_COLUMNS} rows={6} />
              ) : (analyticsData?.weekends ?? []).length === 0 ? (
                <TableEmptyRow
                  colSpan={TREND_COLUMNS}
                  title="No weekends in this range"
                  body="Widen the range to see how signups and check-ins have trended."
                />
              ) : (
                (analyticsData?.weekends ?? []).map((weekend) => {
                  const range = formatWeekendRange(weekend.weekendStartDate, holidayDayFromLabel(weekend.weekendLabel));
                  return (
                    <TableRow key={weekend.weekendStartDate}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="labelMd" component="span" sx={{ color: sbg.text.primary }}>
                          {range}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(weekend.totalSignedUp)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.totalWithoutShifts)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.oneShiftCount)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.twoPlusShiftCount)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.threePlusShiftCount)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.missedStartCount)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.missedEndCount)}</TableCell>
                      <TableCell align="right" sx={{ width: 56 }}>
                        <Tooltip title="Open roster">
                          <IconButton size="small" aria-label={`Open roster for ${range}`} onClick={() => onViewWeekend(weekend.weekendStartDate)}>
                            <ArrowRight size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableCard>
      )}

      {!failedFirstLoad && (
        <Box sx={{ display: 'grid', gap: 3, alignItems: 'start', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
          <LeaderCard
            title="Most shifts signed up"
            description={`Top 10 · ${span}`}
            items={topSignupEmployees}
            value={(employee) => employee.totalShiftSignups}
            display={(amount) => formatCount(amount, 'shift')}
            caption={(employee) => `Across ${formatCount(employee.weekendsSignedUp, 'weekend')}`}
            emptyTitle="No signups in this range"
            emptyBody="Reps who sign up for weekend shifts are ranked here."
            pending={leaderPending}
          />
          <SectionCard
            title="No signups in this range"
            description={span}
            action={analyticsData ? <CountBadge count={zeroSignupEmployees.length} max={999} label={formatCount(zeroSignupEmployees.length, 'rep')} /> : undefined}
          >
            {leaderPending ? (
              <Box aria-hidden="true" sx={{ display: 'grid', gap: 1.5, visibility: showSkeleton ? 'visible' : 'hidden' }}>
                {[0, 1, 2, 3, 4].map((n) => (
                  <Skeleton key={n} variant="rounded" height={24} />
                ))}
              </Box>
            ) : zeroSignupEmployees.length === 0 ? (
              <EmptyState compact title="Every rep signed up at least once" body="Reps with no weekend shifts in the range appear here." />
            ) : (
              <Box
                component="ul"
                tabIndex={0}
                aria-label="Reps with no signups"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  maxHeight: 340,
                  overflow: 'auto',
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                  columnGap: 2,
                  rowGap: 1.5,
                  '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: 2, borderRadius: sbg.radius.md },
                }}
              >
                {zeroSignupEmployees.map((employee) => (
                  <Box component="li" key={employee.employeeId} sx={{ minWidth: 0 }}>
                    <PersonCell name={employee.name} secondary={employee.teamName || 'Sales'} size={24} />
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>
          <LeaderCard
            title="Most missed start check-ins"
            description={`Top 10 · ${span}`}
            items={missedStartLeaders}
            value={(employee) => employee.missedStartCount}
            display={(amount) => formatCount(amount, 'check-in')}
            caption={(employee) => `${formatCount(employee.totalShiftSignups, 'shift')} signed up`}
            emptyTitle="No missed starts"
            emptyBody="Every start check-in in this range was confirmed or is still open."
            pending={leaderPending}
            variant="list"
          />
          <LeaderCard
            title="Most missed end check-ins"
            description={`Top 10 · ${span}`}
            items={missedEndLeaders}
            value={(employee) => employee.missedEndCount}
            display={(amount) => formatCount(amount, 'check-in')}
            caption={(employee) => `${formatCount(employee.totalShiftSignups, 'shift')} signed up`}
            emptyTitle="No missed ends"
            emptyBody="Every end check-in in this range was confirmed or is still open."
            pending={leaderPending}
            variant="list"
          />
        </Box>
      )}
    </Box>
  );
};

export default HistoryTab;
